import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Users, GraduationCap, MapPin, Navigation, Phone, Mail,
  CheckCircle2, AlertTriangle, Building2, Globe2, ChevronLeft,
  TrendingUp, ShieldCheck, Crosshair,
} from "lucide-react";
import type { Catchment, CatchmentSchool } from "./types";
import { getSchoolFinance, fmtUSD, fmtNum, fmtPct } from "./finance";
import { SCHOOL_LEVEL_COLORS } from "./markers";

const C = {
  forest: "#14532D",
  forestDeep: "#0C3320",
  forestMid: "#1E7A45",
  terracotta: "#B33A1A",
  mustard: "#C4A882",
  sky: "#0284c7",
  amber: "#d97706",
  emerald: "#059669",
  rose: "#e11d48",
  text: "#1a2e23",
  textMuted: "#5a7a6a",
  textLight: "#8aaa9a",
  bg: "#F6FAF8",
  card: "#FFFFFF",
  border: "#E0EDE7",
};

const OWNERSHIP_LABELS: Record<string, string> = {
  government: "Government", private: "Private", community: "Community", other: "Other",
};

const BOARDING_LABELS: Record<string, string> = {
  day: "Day", boarding: "Boarding", mixed: "Day & Boarding",
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  verified: { label: "GPS Verified", color: "#059669" },
  imported: { label: "GPS Imported", color: "#0284c7" },
  approximate: { label: "Approximate", color: "#d97706" },
  needs_verification: { label: "Needs Verification", color: "#d97706" },
  missing: { label: "Missing Location", color: "#dc2626" },
};

export function SchoolModal({
  school, catchment, onClose, onBack,
}: {
  school: CatchmentSchool;
  catchment: Catchment | null;
  onClose: () => void;
  onBack: () => void;
}) {
  const finance = useMemo(() => getSchoolFinance(school), [school]);
  const levelColor = SCHOOL_LEVEL_COLORS[school.education_level ?? "other"] ?? C.forest;
  const status = STATUS_LABELS[school.location_status ?? "imported"] ?? STATUS_LABELS.imported;
  const capacityPct = school.capacity && school.capacity > 0
    ? Math.min(100, Math.round((school.student_count / school.capacity) * 100))
    : null;
  const genderRatio = school.student_count > 0
    ? Math.round((school.male_student_count / school.student_count) * 100)
    : 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[85] flex justify-end"
        style={{ background: "rgba(0,0,0,0.5)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="relative h-full w-full max-w-[480px] overflow-y-auto bg-white shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* ── Hero ── */}
          <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${levelColor}, ${levelColor}cc)` }}>
            <div className="absolute inset-0 opacity-[0.06]" style={{
              backgroundImage: `radial-gradient(circle at 30% 50%, white 0%, transparent 50%)`,
            }} />
            <div className="relative p-6">
              <div className="flex items-start justify-between">
                <button onClick={onBack} className="flex items-center gap-1 text-xs text-white/50 transition hover:text-white/80">
                  <ChevronLeft className="h-4 w-4" /> Back to Catchment
                </button>
                <button onClick={onClose} className="rounded-lg p-2 transition hover:bg-white/10">
                  <X className="h-5 w-5 text-white/60" />
                </button>
              </div>

              {catchment && (
                <div className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/40">
                  <Globe2 className="h-3 w-3" />
                  {catchment.name}
                  <ChevronLeft className="h-3 w-3 rotate-180" />
                  <span className="text-white/70">{school.name}</span>
                </div>
              )}

              <h2 className="mt-2 text-xl font-bold tracking-tight text-white">{school.name}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-white/60">
                <span className="capitalize">{school.education_level ?? "school"}</span>
                <span>·</span>
                <span>{OWNERSHIP_LABELS[school.ownership ?? "other"] ?? "—"}</span>
                <span>·</span>
                <span>{BOARDING_LABELS[school.boarding_type ?? "day"] ?? "—"}</span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                <div>
                  <p className="text-2xl font-bold text-white">{fmtNum(school.student_count)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Students</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{fmtNum(school.teacher_count)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Teachers</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{fmtUSD(finance.annualFees)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Economy</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Financial card */}
            <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Annual School Economy</p>
              <p className="text-2xl font-bold" style={{ color: C.forest }}>{fmtUSD(finance.annualFees)}</p>
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: C.textMuted }}>Collected</span>
                  <span className="font-semibold" style={{ color: C.emerald }}>{fmtUSD(finance.collected)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full" style={{ background: C.border }}>
                  <div className="h-full rounded-full" style={{ width: `${finance.collectionRate * 100}%`, background: C.emerald }} />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: C.textMuted }}>Outstanding</span>
                  <span className="font-semibold" style={{ color: C.rose }}>{fmtUSD(finance.outstanding)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: C.textMuted }}>Collection Rate</span>
                  <span className="font-bold" style={{ color: finance.collectionRate > 0.8 ? C.emerald : finance.collectionRate > 0.6 ? C.sky : C.amber }}>
                    {fmtPct(finance.collectionRate)}
                  </span>
                </div>
              </div>
            </div>

            {/* Location card */}
            <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Location</p>
                <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: `${status.color}15`, color: status.color }}>
                  {school.location_status === "verified" ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                  {status.label}
                </span>
              </div>
              <div className="space-y-1.5 text-xs">
                {school.latitude != null && school.longitude != null && (
                  <>
                    <div className="flex justify-between"><span style={{ color: C.textMuted }}>Latitude</span><span className="font-mono" style={{ color: C.text }}>{school.latitude.toFixed(4)}°</span></div>
                    <div className="flex justify-between"><span style={{ color: C.textMuted }}>Longitude</span><span className="font-mono" style={{ color: C.text }}>{school.longitude.toFixed(4)}°</span></div>
                  </>
                )}
                <div className="flex justify-between"><span style={{ color: C.textMuted }}>District</span><span className="font-medium" style={{ color: C.text }}>{school.district ?? "—"}</span></div>
                <div className="flex justify-between"><span style={{ color: C.textMuted }}>Subcounty</span><span className="font-medium" style={{ color: C.text }}>{school.subcounty ?? "—"}</span></div>
                <div className="flex justify-between"><span style={{ color: C.textMuted }}>Parish</span><span className="font-medium" style={{ color: C.text }}>{school.parish ?? "—"}</span></div>
                {catchment && (
                  <div className="flex justify-between"><span style={{ color: C.textMuted }}>Catchment</span><span className="font-medium" style={{ color: C.forest }}>{catchment.name}</span></div>
                )}
              </div>
              {school.latitude && school.longitude && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${school.latitude},${school.longitude}`}
                  target="_blank" rel="noreferrer"
                  className="mt-3 flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium text-white transition hover:opacity-90"
                  style={{ background: C.forest }}
                >
                  <Navigation className="h-3.5 w-3.5" /> View on Map
                </a>
              )}
            </div>

            {/* Catchment relationship */}
            {catchment && (
              <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Primary Catchment</p>
                <div className="flex items-center gap-2">
                  <Globe2 className="h-4 w-4" style={{ color: C.forest }} />
                  <span className="text-sm font-semibold" style={{ color: C.text }}>{catchment.name}</span>
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between"><span style={{ color: C.textMuted }}>Relationship</span><span className="font-medium" style={{ color: C.text }}>Geographically Assigned</span></div>
                  <div className="flex justify-between"><span style={{ color: C.textMuted }}>Status</span><span style={{ color: school.location_status === "verified" ? C.emerald : C.amber }}>{school.location_status === "verified" ? "Verified" : "Needs Verification"}</span></div>
                </div>
              </div>
            )}

            {/* Capacity & gender */}
            {capacityPct !== null && (
              <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                <div className="mb-2 flex items-center justify-between text-xs">
                  <span style={{ color: C.textMuted }}>Capacity Utilisation</span>
                  <span className="font-semibold" style={{ color: capacityPct > 90 ? C.rose : capacityPct > 70 ? C.amber : C.emerald }}>{capacityPct}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full" style={{ background: C.border }}>
                  <div className="h-full rounded-full" style={{ width: `${capacityPct}%`, background: capacityPct > 90 ? C.rose : capacityPct > 70 ? C.amber : C.emerald }} />
                </div>
              </div>
            )}

            {school.student_count > 0 && (
              <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Gender Split</p>
                <div className="flex h-2 overflow-hidden rounded-full" style={{ background: C.border }}>
                  <div className="h-full" style={{ width: `${genderRatio}%`, background: C.sky }} />
                  <div className="h-full" style={{ width: `${100 - genderRatio}%`, background: C.rose }} />
                </div>
                <div className="mt-1 flex justify-between text-[10px]" style={{ color: C.textMuted }}>
                  <span>{fmtNum(school.male_student_count)} male</span>
                  <span>{fmtNum(school.female_student_count)} female</span>
                </div>
              </div>
            )}

            {/* Contact */}
            <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Contact</p>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span style={{ color: C.textMuted }}>Code</span><span className="font-mono" style={{ color: C.text }}>{school.code ?? "—"}</span></div>
                <div className="flex justify-between"><span style={{ color: C.textMuted }}>Status</span><span style={{ color: school.status === "active" ? C.emerald : C.rose }}>{school.status === "active" ? "Active" : "Inactive"}</span></div>
                {school.phone && (
                  <a href={`tel:${school.phone}`} className="flex items-center gap-1.5 transition hover:underline" style={{ color: C.sky }}>
                    <Phone className="h-3 w-3" /> {school.phone}
                  </a>
                )}
                {school.email && (
                  <a href={`mailto:${school.email}`} className="flex items-center gap-1.5 transition hover:underline" style={{ color: C.sky }}>
                    <Mail className="h-3 w-3" /> {school.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
